import React, { useState, useEffect } from 'react';
import { TrendingUp, Building2, Heart, Pill, BookOpen, Globe, Download, RefreshCw, Users, Activity, BarChart3, Shield } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const MetricCard = ({ icon: Icon, title, value, subtitle, color = "pink", children }) => (
  <Card className={`p-5 bg-gradient-to-br from-${color}-500/10 to-${color}-600/5 border-${color}-400/20`}>
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className={`w-10 h-10 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-300`} />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">{title}</p>
          {subtitle && <p className="text-pink-200/70 text-xs">{subtitle}</p>}
        </div>
      </div>
      {value !== undefined && <span className="text-2xl font-bold text-white">{value}</span>}
    </div>
    {children}
  </Card>
);

const InstitutionalReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/metrics/institutional-report');
      setReport(res.data);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger le rapport', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const exportCSV = () => {
    if (!report) return;
    const r = report;
    let csv = "Metrique,Valeur\n";
    csv += `Total Utilisateurs,${r.populationCoverage.totalUsers}\n`;
    csv += `Utilisateurs Actifs (7j),${r.populationCoverage.activeUsersWeekly}\n`;
    csv += `Utilisateurs Actifs (30j),${r.populationCoverage.activeUsersMonthly}\n`;
    csv += `Taux Retention 7j,${r.populationCoverage.retentionRate7d}%\n`;
    csv += `Taux Retention 30j,${r.populationCoverage.retentionRate30d}%\n`;
    csv += `Utilisatrices Suivi Cycle,${r.healthIndicators.cycleTracking.usersTracking}\n`;
    csv += `Duree Moyenne Cycle,${r.healthIndicators.cycleTracking.avgCycleLength} jours\n`;
    csv += `Taux Cycles Reguliers,${r.healthIndicators.cycleTracking.regularCycleRate}%\n`;
    csv += `Utilisatrices Contraception,${r.healthIndicators.contraception.usersTracking}\n`;
    csv += `Taux Adoption Contraception,${r.healthIndicators.contraception.adoptionRate}%\n`;
    r.healthIndicators.contraception.methodDistribution.forEach(m => {
      csv += `Contraception - ${m.method},${m.count} (${m.pct}%)\n`;
    });
    csv += `Modules Completes,${r.educationImpact.modulesCompleted}\n`;
    csv += `Quiz Tentes,${r.educationImpact.quizAttempts}\n`;
    csv += `Taux Reussite Quiz,${r.educationImpact.quizPassRate}%\n`;
    csv += `Score Moyen Quiz,${r.educationImpact.avgScore}%\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-fleurdevie-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Rapport CSV telecharge' });
  };

  if (loading) return <div className="text-center text-pink-200 py-12">Chargement du rapport...</div>;
  if (!report) return <div className="text-center text-pink-200 py-12">Rapport indisponible</div>;

  const r = report;

  return (
    <div className="space-y-6" data-testid="institutional-report">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Globe className="w-6 h-6 text-cyan-300" />
            Rapport Institutionnel
          </h2>
          <p className="text-pink-200 text-sm mt-1">Metriques a valeur marchande pour partenaires institutionnels</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={fetchReport} className="bg-white/10 hover:bg-white/20 text-white" data-testid="refresh-report">
            <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
          </Button>
          <Button size="sm" onClick={exportCSV} className="bg-green-600/30 hover:bg-green-600/50 text-green-200" data-testid="export-csv">
            <Download className="w-4 h-4 mr-1" /> Exporter CSV
          </Button>
        </div>
      </div>

      {/* Coverage & Growth */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-300" /> Couverture Population
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 bg-blue-500/10 border-blue-400/20 text-center">
            <p className="text-3xl font-bold text-white">{r.populationCoverage.totalUsers}</p>
            <p className="text-blue-200 text-xs">Total Utilisateurs</p>
          </Card>
          <Card className="p-4 bg-green-500/10 border-green-400/20 text-center">
            <p className="text-3xl font-bold text-green-300">{r.populationCoverage.activeUsersWeekly}</p>
            <p className="text-green-200 text-xs">Actifs (7 jours)</p>
          </Card>
          <Card className="p-4 bg-cyan-500/10 border-cyan-400/20 text-center">
            <p className="text-3xl font-bold text-cyan-300">{r.populationCoverage.retentionRate7d}%</p>
            <p className="text-cyan-200 text-xs">Retention 7j</p>
          </Card>
          <Card className="p-4 bg-purple-500/10 border-purple-400/20 text-center">
            <p className="text-3xl font-bold text-purple-300">{r.populationCoverage.retentionRate30d}%</p>
            <p className="text-purple-200 text-xs">Retention 30j</p>
          </Card>
        </div>

        {/* Growth Trend */}
        {r.populationCoverage.growthTrend.length > 0 && (
          <Card className="mt-3 p-4 bg-white/5 border-white/10">
            <p className="text-white font-semibold text-sm mb-3">Tendance de Croissance (6 mois)</p>
            <div className="flex items-end gap-2 h-24">
              {r.populationCoverage.growthTrend.map((m, i) => {
                const max = Math.max(...r.populationCoverage.growthTrend.map(t => t.newUsers), 1);
                const height = Math.max((m.newUsers / max) * 100, 5);
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-white text-xs font-bold">{m.newUsers}</span>
                    <div className="w-full bg-gradient-to-t from-pink-500 to-purple-400 rounded-t" style={{ height: `${height}%` }} />
                    <span className="text-pink-200/60 text-[10px]">{m.month.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Health Indicators */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-300" /> Indicateurs de Sante
          <span className="text-xs bg-red-500/20 text-red-200 px-2 py-0.5 rounded-full">OMS / Min. Sante</span>
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Cycle Tracking */}
          <Card className="p-5 bg-red-500/5 border-red-400/20">
            <h4 className="text-white font-semibold mb-3">Suivi Menstruel</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-pink-200 text-sm">Utilisatrices</span>
                <span className="text-white font-bold">{r.healthIndicators.cycleTracking.usersTracking}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-pink-200 text-sm">Taux d'adoption</span>
                <span className="text-green-300 font-bold">{r.healthIndicators.cycleTracking.adoptionRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-pink-200 text-sm">Duree moyenne cycle</span>
                <span className="text-white font-bold">{r.healthIndicators.cycleTracking.avgCycleLength}j</span>
              </div>
              <div className="flex justify-between">
                <span className="text-pink-200 text-sm">Cycles reguliers</span>
                <span className="text-cyan-300 font-bold">{r.healthIndicators.cycleTracking.regularCycleRate}%</span>
              </div>
              <div className="p-2 bg-blue-500/10 border border-blue-400/20 rounded text-blue-200 text-xs">
                {r.healthIndicators.cycleTracking.WHO_benchmark}
              </div>
            </div>
          </Card>

          {/* Contraception */}
          <Card className="p-5 bg-purple-500/5 border-purple-400/20">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              Contraception
              <span className="text-xs bg-purple-500/20 text-purple-200 px-2 py-0.5 rounded-full">Pharma</span>
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-pink-200 text-sm">Utilisatrices</span>
                <span className="text-white font-bold">{r.healthIndicators.contraception.usersTracking}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-pink-200 text-sm">Taux d'adoption</span>
                <span className="text-green-300 font-bold">{r.healthIndicators.contraception.adoptionRate}%</span>
              </div>
              {r.healthIndicators.contraception.methodDistribution.map(m => (
                <div key={m.method} className="flex items-center gap-2">
                  <span className="text-pink-200 text-xs flex-1">{m.method}</span>
                  <Progress value={m.pct} className="flex-1 h-2 bg-purple-900/50" />
                  <span className="text-white text-xs w-16 text-right">{m.count} ({m.pct}%)</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Education Impact */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-yellow-300" /> Impact Educatif
          <span className="text-xs bg-yellow-500/20 text-yellow-200 px-2 py-0.5 rounded-full">ONG / Bailleurs</span>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 bg-yellow-500/10 border-yellow-400/20 text-center">
            <p className="text-2xl font-bold text-yellow-300">{r.educationImpact.modulesCompleted}</p>
            <p className="text-yellow-200 text-xs">Modules completes</p>
          </Card>
          <Card className="p-4 bg-orange-500/10 border-orange-400/20 text-center">
            <p className="text-2xl font-bold text-orange-300">{r.educationImpact.quizAttempts}</p>
            <p className="text-orange-200 text-xs">Quiz tentes</p>
          </Card>
          <Card className="p-4 bg-green-500/10 border-green-400/20 text-center">
            <p className="text-2xl font-bold text-green-300">{r.educationImpact.quizPassRate}%</p>
            <p className="text-green-200 text-xs">Taux reussite</p>
          </Card>
          <Card className="p-4 bg-cyan-500/10 border-cyan-400/20 text-center">
            <p className="text-2xl font-bold text-cyan-300">{r.educationImpact.avgScore}%</p>
            <p className="text-cyan-200 text-xs">Score moyen</p>
          </Card>
        </div>
      </div>

      {/* Geographic Distribution */}
      {r.geographicDistribution.topSchools.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-300" /> Distribution Geographique
          </h3>
          <Card className="p-4 bg-white/5 border-white/10">
            <div className="space-y-2">
              {r.geographicDistribution.topSchools.map((s, i) => {
                const maxUsers = r.geographicDistribution.topSchools[0]?.users || 1;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-pink-200 text-sm w-48 truncate">{s.name}</span>
                    <div className="flex-1">
                      <div className="h-4 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full" style={{ width: `${(s.users / maxUsers) * 100}%` }} />
                      </div>
                    </div>
                    <span className="text-white font-bold text-sm w-12 text-right">{s.users}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Stakeholder Value Cards */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-300" /> Valeur pour les Partenaires
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          {Object.entries(r.stakeholderValue).map(([key, val]) => {
            const icons = { WHO_OMS: Globe, ministereSante: Building2, pharmaceutiques: Pill, ONG_institutions: BookOpen };
            const colors = { WHO_OMS: 'cyan', ministereSante: 'blue', pharmaceutiques: 'purple', ONG_institutions: 'yellow' };
            const labels = { WHO_OMS: 'OMS', ministereSante: 'Min. Sante', pharmaceutiques: 'Pharmaceutiques', ONG_institutions: 'ONG & Institutions' };
            const Ic = icons[key] || Globe;
            const c = colors[key] || 'pink';
            return (
              <Card key={key} className={`p-4 bg-${c}-500/5 border-${c}-400/20`}>
                <div className="flex items-center gap-2 mb-2">
                  <Ic className={`w-5 h-5 text-${c}-300`} />
                  <span className="text-white font-semibold">{labels[key]}</span>
                </div>
                <p className="text-pink-200 text-xs mb-2">{val.relevance}</p>
                <div className="flex flex-wrap gap-1">
                  {val.metrics.map((m, i) => (
                    <span key={i} className="px-2 py-0.5 bg-white/10 text-pink-100 text-[10px] rounded-full">{m}</span>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <p className="text-pink-200/50 text-xs text-center">Rapport genere le {new Date(r.generatedAt).toLocaleString('fr-FR')}</p>
    </div>
  );
};

export default InstitutionalReport;
